import { useActiveOrganization } from "@/context/ActiveOrganizationProvider";
import useConfirm, { ConfirmType } from "@/context/useConfirm";
import { fileHasPage, getFileHref, useAppHref } from "@/lib/path-utils";
import { useRequireAuth } from "@/lib/use-auth";
import { checkCanUserDeploy } from "@/lib/user";
import {
  
  deleteVersion,
  downloadSerializedFile,
  FileEnvironment,
  serializeFileById,
  useFileEnvironments,
  useVersions} from "@/services/files.service";
import { File } from "@/types/app/file";
import { PencilIcon } from "@components/library/Icons";
import { hlToast, ToastVariant } from "@components/library/Toast";
import { ConditionalTooltip, CONTACT_ADMIN_TOOLTIP } from "@components/library/Tooltip";
import {
  DIFF_QUERY_MODIFIED,
  DIFF_QUERY_ORIGINAL,
  VersionDiffModal,
} from "@components/llama/Dashboard/VersionDiffModal";
import { CubeIcon, DocumentTextIcon, SaveIcon, TrashIcon, UploadIcon } from "@heroicons/react/outline";
import { RocketLaunch } from "@phosphor-icons/react";
import { CategoricalTableFilterState, useLlamaPageContext } from "context/LlamaPageContext";
import Link from "next/link";
import { useRouter } from "next/router";
import { parseAsString, useQueryState } from "nuqs";
import { Dispatch, SetStateAction } from "react";
import { Action } from "../ActionsColumn/ActionsMenu";
import { ContainsButLooksLikeIsOperator, Filter } from "@/lib/filtering/filters";

export const useVersionTableActions = (
  file?: File,
  /**
   * This is used to the set the version in the deployment dialog.
   * Note that deployment dialog must be rendered in the component that calls this hook.
   */
  setVersionForDeployment?: Dispatch<SetStateAction<File | null>>,
  /**
   * This is used to the set the version in the export dialog.
   * Note that export dialog must be rendered in the component that calls this hook.
   */
  setVersionToExport?: Dispatch<SetStateAction<File | null>>,
): Action<File>[] => {
  const { user } = useRequireAuth();
  const { fileEnvironments } = useFileEnvironments(file?.id);
  const { slug } = useActiveOrganization();

  const { mutate: mutateVersions } = useVersions(file?.id);

  const fileIsPrompt = file?.type === "prompt";
  const fileIsAgent = file?.type === "agent";
  const fileIsTool = file?.type === "tool";
  const fileIsDataset = file?.type === "dataset";

  const { filters, setFilters } = useLlamaPageContext();
  const confirm = useConfirm();

  const canDeploy = checkCanUserDeploy(user);

  const compareToDeployedAction = useCompareToDeployedAction(fileEnvironments);
  const router = useRouter();
  const logsHref = useAppHref({
    type: "file",
    id: file?.id ?? "",
    page: "logs",
  });
  const goToLogs = () => {
    router.push(logsHref);
  };

  const actions: (Action<File> | false)[] = [
    file !== null &&
      file !== undefined &&
      fileHasPage(file, "editor") &&
      getOpenInEditorAction({
        fileId: file.id,
        slug: slug as string,
      }),
    !fileIsDataset && getViewDataAction({ filters, setFilters, goToLogs }),

    fileIsPrompt && compareToDeployedAction,
    getDeployAction({
      setVersionForDeployment,
      fileEnvironments,
      canDeploy,
    }),
    file && !fileIsTool && !fileIsDataset ? { type: "divider" } : false,
    (fileIsPrompt || fileIsAgent) && getExportFileAction({ fileType: file.type }),
    fileIsDataset && getExportAsCsvAction({ setVersionToExport }),
    // TODO: Re-enable when we have a safe way to delete tool configs
    file && !fileIsTool ? getDeleteAction({ file, mutateVersions, confirm }) : false,
  ];

  return actions.filter(Boolean) as Action<File>[];
};

const getOpenInEditorAction = ({ fileId, slug }: { fileId: string; slug: string }): Action<File> => ({
  menuItemProps: () => {
    return {
      children: "Open in Editor",
      IconLeft: PencilIcon,
    };
  },
  Wrapper: ({ children, data: version }) => (
    <Link href={`${getFileHref({ slug, id: fileId, page: "editor" })}/${version.version_id}`}>{children}</Link>
  ),
});

const getViewDataAction = ({
  filters,
  setFilters,
  goToLogs,
}: {
  filters: Filter[];
  setFilters: React.Dispatch<React.SetStateAction<Filter[]>>;
  goToLogs: () => void;
}): Action<File> => ({
  menuItemProps: ({ data: version }) => {
    return {
      children: "View Logs",
      IconLeft: CubeIcon,
      onClick: () => {
        const newFilter: Filter = {
          target: {
            target: "version_id",
            type: "categorical_multi",
            label: "Version ID",
            errorState: false,
          },
          operator: ContainsButLooksLikeIsOperator,
          type: "categorical_multi" as const,
          value: [version.version_id],
        };
        setFilters([...filters, newFilter]);
        goToLogs();
      },
    };
  },
});

const useCompareToDeployedAction = (fileEnvironments?: FileEnvironment[]): Action<File> => {
  const [originalVersionId, setOriginalVersionId] = useQueryState(DIFF_QUERY_ORIGINAL, parseAsString);
  const [modifiedVersionId, setModifiedVersionId] = useQueryState(DIFF_QUERY_MODIFIED, parseAsString);

  return {
    menuItemProps: ({ data: version }) => {
      const defaultVersion = fileEnvironments?.find((env) => env.tag === "default")?.file;
      const versionId = version.version_id;
      const originalVersionId = defaultVersion?.version_id || versionId; // Used as reference for comparison

      return {
        children: "Compare...",
        IconLeft: DocumentTextIcon,
        onClick: async () => {
          setOriginalVersionId(originalVersionId);
          setModifiedVersionId(versionId);
        },
      };
    },
  };
};

const getExportAsCsvAction = ({
  setVersionToExport,
}: {
  setVersionToExport?: Dispatch<SetStateAction<File | null>>;
}): Action<File> => ({
  menuItemProps: ({ data: version }) => {
    const canExport = "datapoints_count" in version && version.datapoints_count > 0;
    return {
      disabled: !canExport,
      children: (
        <ConditionalTooltip condition={!canExport} content={"No datapoints to export"}>
          <div className="flex items-center">Export</div>
        </ConditionalTooltip>
      ),
      IconLeft: SaveIcon,
      onClick: async () => {
        setVersionToExport?.(version);
      },
    };
  },
});

const getDeployAction = ({
  setVersionForDeployment,
  fileEnvironments,
  canDeploy,
}: {
  setVersionForDeployment?: Dispatch<SetStateAction<File | null>>;
  fileEnvironments?: FileEnvironment[];
  canDeploy: boolean;
}): Action<File> => ({
  menuItemProps: ({ data: fileVersion }) => {
    const isDeployed =
      fileEnvironments && fileEnvironments.some((env) => env.file && env.file.version_id === fileVersion?.version_id);
    const isDeployedToAll =
      fileEnvironments && fileEnvironments.every((env) => env.file && env.file.version_id === fileVersion?.version_id);

    const menuItemContent = <div className="flex items-center gap-4">Deploy...</div>;

    return {
      children: (
        <ConditionalTooltip condition={!canDeploy} content={CONTACT_ADMIN_TOOLTIP}>
          {menuItemContent}
        </ConditionalTooltip>
      ),
      IconLeft: { Icon: RocketLaunch },
      disabled: isDeployedToAll || !canDeploy,
      onClick: () => setVersionForDeployment?.(fileVersion),
    };
  },
});

const getExportFileAction = ({ fileType }: { fileType: "prompt" | "agent" }): Action<File> => ({
  menuItemProps: ({ data: version }) => {
    return {
      children: `Export .${fileType}`,
      IconLeft: UploadIcon,
      onClick: async () => {
        downloadSerializedFile({
          serializedFile: (await serializeFileById(version.id, { versionId: version.version_id })).data,
          fileStem: version.version_id,
          fileType: fileType,
        });
      },
    };
  },
});

const getDeleteAction = ({
  file,
  mutateVersions,
  confirm,
}: {
  file: File;
  mutateVersions: () => void;
  confirm: ConfirmType;
}): Action<File> => ({
  menuItemProps: ({ data: version }) => {
    const canBeDeleted = true;
    return {
      children: "Delete",
      IconLeft: TrashIcon,
      onClick: async () => {
        try {
          await confirm({
            title: "Are you sure you want to delete this Version?",
            content: (
              <>
                This will delete all associated logs from this file.
                <br />
                <b>This action cannot be undone.</b>
              </>
            ),
            confirmButtonProps: {
              shade: "red",
              styling: "outline",
            },
            confirmationText: "Delete",
            cancellationText: "Cancel",
          });
          await deleteVersion(file.id, version.version_id);
          mutateVersions();
          hlToast({
            title: `Deleted Version '${version?.version_name || version?.version_id}'`,
            variant: ToastVariant.Info,
          });
        } catch (e: any) {
          const errorDetail = e?.response?.data?.detail;
          if (errorDetail) {
            hlToast({
              title: errorDetail.msg || "Failed to delete Version",
              description: errorDetail.description || "Please contact us.",
              variant: ToastVariant.Info,
            });
          }
        }
      },
      disabled: !canBeDeleted,
    };
  },
});
